import { fetchWithAuth } from "../../api/http";

// Mirrors backend totalview_schemas.api.portfolio.PortfolioYearAggregate
export interface PortfolioYearAggregate {
  year: number;
  project_count: number;
  projects_with_data: number;
  dalys: number;
  pdfs: number;
  usd: number;
  total_generation_mwh: number;
  tsdb_available: boolean;
}

// Mirrors backend PortfolioLifetimeAggregate
export interface PortfolioLifetimeAggregate {
  project_count: number;
  projects_projected: number;
  lifetime_years: number;
  social_discount_rate: number;
  dalys_nominal: number;
  dalys_npv: number;
  pdfs_nominal: number;
  pdfs_npv: number;
  usd_nominal: number;
  usd_npv: number;
  total_generation_mwh: number;
}

function buildFilterParams(filters?: Record<string, string>): string {
  if (!filters || Object.keys(filters).length === 0) return "";
  return Object.entries(filters)
    .map(([k, v]) => `filter[${encodeURIComponent(k)}]=${encodeURIComponent(v)}`)
    .join("&");
}

export function getPortfolioAggregateByYear(
  token: string,
  year: number,
  filters?: Record<string, string>,
): Promise<PortfolioYearAggregate> {
  const parts = [`year=${year}`];
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  return fetchWithAuth<PortfolioYearAggregate>(
    `/portfolio/aggregate-by-year?${parts.join("&")}`,
    token,
  );
}

export interface AirshedRegion {
  id: string;
  name: string;
  value_sum: number;
  share_pct: number;
  rank: number;
}

export interface AirshedAggregateResult {
  regions: AirshedRegion[];
  /** Raw GeoJSON FeatureCollection with state/CBSA polygons + properties —
   * kept so the deck can draw a choropleth, not just a ranked table. */
  featureCollection: {
    type: "FeatureCollection";
    features: Array<{
      properties: Record<string, unknown>;
      geometry: { type: string; coordinates: unknown };
    }>;
  } | null;
}

/** Top regions by aggregated airshed PM2.5 intake for the filtered set,
 *  with the raw FeatureCollection (geometry) preserved for choropleth use. */
export async function getPortfolioAirshedAggregate(
  token: string,
  level: "state" | "county" | "cbsa",
  filters?: Record<string, string>,
): Promise<AirshedAggregateResult> {
  const parts = [`level=${level}`];
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  try {
    const fc = await fetchWithAuth<AirshedAggregateResult["featureCollection"]>(
      `/portfolio/airshed-intensity-aggregate-filtered?${parts.join("&")}`,
      token,
    );
    const regions = (fc?.features ?? [])
      .map((f) => ({
        id: String(f.properties.id ?? ""),
        name: String(f.properties.name ?? f.properties.id ?? "—"),
        value_sum: Number(f.properties.value_sum ?? 0),
        share_pct: Number(f.properties.share_pct ?? 0),
        rank: Number(f.properties.rank ?? 0),
      }))
      .sort((a, b) => a.rank - b.rank);
    return { regions, featureCollection: fc ?? null };
  } catch {
    return { regions: [], featureCollection: null };
  }
}

export function getPortfolioLifetime(
  token: string,
  filters?: Record<string, string>,
  opts?: { lifetimeYears?: number; discountRate?: number },
): Promise<PortfolioLifetimeAggregate> {
  const parts: string[] = [];
  if (opts?.lifetimeYears != null)
    parts.push(`project_lifetime_years=${opts.lifetimeYears}`);
  if (opts?.discountRate != null)
    parts.push(`social_discount_rate=${opts.discountRate}`);
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return fetchWithAuth<PortfolioLifetimeAggregate>(
    `/portfolio/lifetime${qs}`,
    token,
  );
}
